import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Select,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import React, {useMemo, useState} from "react";
import {useGlobalFilter, usePagination, useSortBy, useTable,} from "react-table";
import Moment from 'moment';

import Card from "components/card/Card";
import {deleteData} from "../../services/api";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {saveAs} from 'file-saver';

import {FaFileCsv, FaTrash,} from "react-icons/fa";
import UserStatus from "../users/UserStatus";
import {USER_STATUS} from "../../variables/userStatus";

export default function UserTable(props) {
    const {columnsData, tableData, onDelete} = props;

    const toast = useToast()
    const MySwal = withReactContent(Swal)

    const [selectedStatuses, setSelectedStatuses] = useState([]);

    const columns = useMemo(() => columnsData, [columnsData]);
    const data = useMemo(() => tableData, [tableData]);
    const filteredData = useMemo(() => {
        return selectedStatuses.length > 0
            ? tableData.filter(row => selectedStatuses.includes(row.status))
            : tableData;
    }, [tableData, selectedStatuses]);

    const initialState = {
        pageSize: 100,
        pageIndex: 0
    };

    const tableInstance = useTable(
        {
            columns,
            data: filteredData,
            initialState
        },
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize},
        setGlobalFilter
    } = tableInstance;

    const handleDeleteClick = (user) => {
        MySwal.fire({
            title: <p>Löschung bestätigen</p>,
            html: <p>Benutzer {user.id} wirklich löschen?</p>,
            confirmButtonText: 'Benutzer jetzt löschen',
            confirmButtonColor: '#d33',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Abbrechen',
            preConfirm: async () => {
                Swal.showLoading();
                return handleConfirmDeleteClick(user.id);
            }
        })
    };

    const handleConfirmDeleteClick = async (userId) => {
        try {
            await deleteData('/api/users/' + userId);
            onDelete(userId);

            toast({
                title: "Benutzer wurde gelöscht.",
                description: `Der Benutzer wurde erfolgreich gelöscht.`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            // setError(error.message);
        }
    };

    const handleSetSearchTerm = (searchTerm) => {
        setGlobalFilter(searchTerm)
    };

    const toggleFilter = (status) => {
        setSelectedStatuses((prev) => {
            return prev.includes(status)
                ? prev.filter((s) => s !== status)
                : [status];
        });
    };

    const downloadCSV = () => {
        if (!page.length) {
            alert("Keine Daten zum Exportieren!");
            return;
        }

        const selectedHeaders = ['id', 'fullname', 'email', 'status'];

        const headers = selectedHeaders.join(";");

        const rows = page.map(row =>
            selectedHeaders.map(header => row.original[header] || '').join(";")
        );

        const csvContent = [headers, ...rows].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "export.csv");
    };

    const textColor = useColorModeValue("secondaryGray.900", "white");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
    return (
        <Card
            direction='column'
            w='100%'
            px='0px'
            overflowX={{sm: "scroll", lg: "hidden"}}>

            <Flex>
                <Flex gap={2} mb={5} alignItems='center' ml={5}>
                    <Badge
                        onClick={() => toggleFilter(USER_STATUS.HASRENTALS)}
                        colorScheme='green'
                        cursor="pointer"
                        transition="all 0.2s"
                        boxShadow={selectedStatuses.includes(USER_STATUS.HASRENTALS) ? "0px 0px 6px rgba(59, 130, 246, 0.5)" : "none"}
                    >
                        {data.filter(user => user.status === USER_STATUS.HASRENTALS).length} mit Inserat
                    </Badge>
                    <Badge
                        onClick={() => toggleFilter(USER_STATUS.NORENTALS)}
                        colorScheme='red'
                        cursor="pointer"
                        transition="all 0.2s"
                        boxShadow={selectedStatuses.includes(USER_STATUS.NORENTALS) ? "0px 0px 6px rgba(59, 130, 246, 0.5)" : "none"}
                    >
                        {data.filter(user => user.status === USER_STATUS.NORENTALS).length} ohne Inserat
                    </Badge>
                    <Badge
                        onClick={() => toggleFilter(USER_STATUS.UNCONFIRMED)}
                        colorScheme='purple'
                        cursor="pointer"
                        transition="all 0.2s"
                        boxShadow={selectedStatuses.includes(USER_STATUS.UNCONFIRMED) ? "0px 0px 6px rgba(59, 130, 246, 0.5)" : "none"}
                    >
                        {data.filter(user => user.status === USER_STATUS.UNCONFIRMED).length} nicht bestätigt
                    </Badge>
                    <Button onClick={downloadCSV}>
                        <FaFileCsv/>
                    </Button>
                </Flex>
                <FormControl width={200} mb='24px' ml={'auto'} mr={5} display='flex' alignItems={'center'}>
                    <FormLabel
                        ms='4px'
                        fontSize='sm'
                        fontWeight='500'
                        color={textColor}
                        htmlFor='search'
                    >
                        Suche
                    </FormLabel>
                    <Input
                        id='search'
                        fontSize='sm'
                        type='text'
                        fontWeight='500'
                        size='lg'
                        onChange={(e) => handleSetSearchTerm(e.target.value)}
                    />
                </FormControl>
            </Flex>

            <Table {...getTableProps()} variant='simple' color='gray.500' mb='24px'>
                <Thead>
                    {headerGroups.map((headerGroup, index) => (
                        <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                            {headerGroup.headers.map((column, index) => (
                                <Th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    pe='10px'
                                    key={index}
                                    borderColor={borderColor}>
                                    <Flex
                                        align='center'
                                        fontSize={{sm: "10px", lg: "12px"}}
                                        color='gray.400'>
                                        {column.render("Header")}
                                    </Flex>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <Tr {...row.getRowProps()} key={index}>
                                {row.cells.map((cell, index) => {
                                    let data;
                                    if (cell.column.Header === "ID") {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                {cell.value}
                                            </Text>
                                        );
                                    } else if (cell.column.Header === "STATUS") {
                                        data = (
                                            <UserStatus status={cell.value}/>
                                        );
                                    } else if (cell.column.Header === "REGISTRIERT AM") {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                {Moment(cell.value).format('DD.MM.YYYY HH:mm')}
                                            </Text>
                                        );
                                    } else {
                                        data = (
                                            <Text>
                                                {cell.value}
                                            </Text>
                                        );
                                    }
                                    return (
                                        <Td
                                            {...cell.getCellProps()}
                                            key={index}
                                            fontSize={{sm: "14px"}}
                                            minW={{sm: "150px", md: "200px", lg: "auto"}}
                                            borderColor='transparent'>
                                            {data}
                                        </Td>
                                    );
                                })}
                                <Td><Button onClick={() => handleDeleteClick(row.original)}><FaTrash/></Button></Td>
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>

            <Box className="pagination" mt={4}>
                <Flex alignItems={'center'} justifyContent="space-between" gap={{base: 5, lg: 20}}
                      direction={{base: "column", lg: 'row'}} px={5}>
                    <HStack spacing={4}>
                        <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage} colorScheme="brand" size="sm">
                            {"<<"}
                        </Button>
                        <Button onClick={() => previousPage()} disabled={!canPreviousPage} colorScheme="brand"
                                size="sm">
                            {"<"}
                        </Button>
                        <Button onClick={() => nextPage()} disabled={!canNextPage} colorScheme="brand" size="sm">
                            {">"}
                        </Button>
                        <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} colorScheme="brand"
                                size="sm">
                            {">>"}
                        </Button>
                    </HStack>

                    <Text>
                        <strong>
                            {pageIndex + 1} von {pageOptions.length}
                        </strong>
                    </Text>

                    <HStack spacing={4}>

                        <Text>Seite: </Text>
                        <Input
                            type="number"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(page);
                            }}
                            width="100px"
                            size="sm"
                        />
                        <Select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            width="fit-content"
                            size="sm"
                        >
                            {[10, 50, 100].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    Zeige {pageSize}
                                </option>
                            ))}
                        </Select>
                    </HStack>
                </Flex>
            </Box>
        </Card>
    );
}
